/**
 * Single completion contract for the Homebuyer dossier wizard and dashboard.
 * A non-empty value is not necessarily valid. This is a UI check; Django must
 * continue validating the final dossier independently.
 */
export const DOSSIER_STEP_CODES = {
  "personal-info": "PERSONAL",
  "financial-info": "FINANCIAL",
  documents: "DOCUMENTS",
  "property-information": "PROPERTY",
  "review-download": "REVIEW",
};

const filled = (value) => value !== null && value !== undefined && String(value).trim() !== "";
const digits = (value) => String(value ?? "").replace(/\D/g, "");
const countryCode = (value) => {
  const country = String(value || "").trim().toUpperCase();
  if (["ES", "SPAIN", "ESPAÑA", "ESPANA"].includes(country)) return "ES";
  if (["BE", "BELGIUM", "BELGIQUE"].includes(country)) return "BE";
  return country;
};

export function isValidIBAN(value) {
  const iban = String(value ?? "").replace(/\s/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  // ISO 13616 mod-97, without unsafe large integers.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const character of rearranged) {
    const numeric = character >= "A" ? String(character.charCodeAt(0) - 55) : character;
    for (const digit of numeric) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

function isValidDate(value) {
  if (!filled(value)) return false;
  const text = String(value).trim();
  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) ||
    text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const iso = text.includes("-");
  const year = Number(iso ? match[1] : match[3]);
  const month = Number(match[2]);
  const day = Number(iso ? match[3] : match[1]);
  if (year < 1900 || year > new Date().getFullYear()) return false;
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day && parsed <= new Date();
}

const emailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
const numberValid = (value, { min = 0, strict = false } = {}) =>
  filled(value) && Number.isFinite(Number(value)) && (strict ? Number(value) > min : Number(value) >= min);

function checkApplicantPersonal(info = {}, primary = false) {
  const errors = [];
  for (const field of ["first_name", "last_name", "date_of_birth", "email"]) {
    if (!filled(info[field])) errors.push(field);
  }
  if (filled(info.date_of_birth) && !isValidDate(info.date_of_birth)) errors.push("Invalid date of birth");
  if (filled(info.email) && !emailValid(info.email)) errors.push("Invalid email address");
  if (primary) {
    for (const field of ["nationality", "country_of_residence", "residency_status", "address", "phone", "marital_status", "dependents", "preferred_contact_method"]) {
      if (!filled(info[field])) errors.push(field);
    }
    if (filled(info.phone) && digits(info.phone).length < 7) errors.push("Invalid phone number");
    if (info.residency_status === "OTHER" && !filled(info.residency_status_other)) errors.push("Residency status (other)");
    if (filled(info.dependents) && (!Number.isInteger(Number(info.dependents)) || Number(info.dependents) < 0)) errors.push("Invalid dependents");
  }
  return errors;
}

function checkApplicantFinancial(info = {}) {
  const errors = [];
  for (const field of ["net_income", "listing_in_cpk", "monthly_expenses", "employment_status", "contract_type", "employment_contracts_count", "politically_exposed_person"]) {
    if (!filled(info[field])) errors.push(field);
  }
  for (const field of ["net_income", "monthly_expenses"]) {
    if (filled(info[field]) && !numberValid(info[field])) errors.push(`Invalid ${field}`);
  }
  if (filled(info.employment_contracts_count) && (!Number.isInteger(Number(info.employment_contracts_count)) || Number(info.employment_contracts_count) < 0)) errors.push("Invalid employment contracts count");
  return errors;
}

function getDocumentFiles(requirement) {
  return requirement?.files || requirement?.document_files || [];
}
function validDocumentFile(file) {
  if (file?.is_active === false) return false;
  if (file?.latest_version?.is_active) return true;
  return (file?.versions || []).some((version) => version?.is_active);
}
export function validateDossierStepCompletion(draft = {}) {
  const result = {};
  const borrowers = draft.borrowers || [];
  const primary = borrowers.find((b) => b?.role === "PRIMARY") || borrowers[0] || {};
  const co = borrowers.find((b) => b?.role === "CO_APPLICANT");
  const personalProblems = checkApplicantPersonal(primary.personal_info || {}, true);
  if (co) personalProblems.push(...checkApplicantPersonal(co.personal_info || {}));
  if (!draft.market_region_id) personalProblems.push("Market region");
  if (!filled(draft.country_of_property_purchase)) personalProblems.push("Country of purchase");
  result["personal-info"] = personalProblems;

  const financialProblems = checkApplicantFinancial(primary.financial_info || {});
  if (co) financialProblems.push(...checkApplicantFinancial(co.financial_info || {}));
  const f = draft.dossier_financial_info || {};
  for (const key of ["equity_amount", "property_price", "tenure_years", "monthly_cost_percent", "repayment_bank_account", "loan_disbursement_bank_account"]) {
    if (!filled(f[key])) financialProblems.push(key);
  }
  if (filled(f.equity_amount) && !numberValid(f.equity_amount)) financialProblems.push("Invalid equity amount");
  if (filled(f.property_price) && !numberValid(f.property_price, { min: 0, strict: true })) financialProblems.push("Invalid property price");
  if (filled(f.equity_amount) && filled(f.property_price) && Number(f.equity_amount) > Number(f.property_price)) financialProblems.push("Equity exceeds property price");
  if (filled(f.tenure_years) && !numberValid(f.tenure_years, { min: 0, strict: true })) financialProblems.push("Invalid tenure");
  if (filled(f.monthly_cost_percent) && !numberValid(f.monthly_cost_percent)) financialProblems.push("Invalid monthly cost percentage");
  for (const key of ["repayment_bank_account", "loan_disbursement_bank_account"]) {
    if (filled(f[key]) && !isValidIBAN(f[key])) financialProblems.push(`Invalid ${key} (IBAN)`);
  }
  result["financial-info"] = financialProblems;

  const documentProblems = [];
  const requirements = Array.isArray(draft.document_requirements) ? draft.document_requirements : [];
  if (!draft.dossier_id || requirements.length === 0) documentProblems.push("Document requirements not loaded");
  for (const req of requirements.filter((item) => item?.is_required === true)) {
    const requiredCount = Math.max(Number(req.min_files ?? 1) || 1, 1);
    const activeCount = getDocumentFiles(req).filter(validDocumentFile).length;
    if (activeCount < requiredCount) documentProblems.push(req.document_type_label || req.document_type_code || `Document ${req.id}`);
  }
  result.documents = documentProblems;

  const p = draft.property_information || {};
  const propertyProblems = [];
  for (const field of ["property_type", "building_condition", "surface_sqm", "year_of_build", "epc_rating_current", "kwh_sqm_year_current", "kwh_sqm_year_target", "heating_type", "cooling_type", "ownership_type", "street_name", "house_number", "city", "postal_code", "country"]) {
    if (!filled(p[field])) propertyProblems.push(field);
  }
  const pc = countryCode(p.country || draft.country_of_property_purchase);
  if (pc === "BE" && !filled(p.epc_rating_target)) propertyProblems.push("Target EPC");
  if (pc === "ES" && !filled(p.land_register_id)) propertyProblems.push("Cadastral reference");
  if (!numberValid(p.surface_sqm, { min: 0, strict: true })) propertyProblems.push("Invalid surface");
  if (filled(p.year_of_build) && (!Number.isInteger(Number(p.year_of_build)) || Number(p.year_of_build) > new Date().getFullYear() || Number(p.year_of_build) < 1700)) propertyProblems.push("Invalid building year");
  if (!numberValid(p.kwh_sqm_year_current, { min: 0, strict: true })) propertyProblems.push("Invalid current kWh/m²/year");
  if (!numberValid(p.kwh_sqm_year_target)) propertyProblems.push("Invalid target kWh/m²/year");
  if (filled(p.kwh_sqm_year_current) && filled(p.kwh_sqm_year_target) && Number(p.kwh_sqm_year_target) >= Number(p.kwh_sqm_year_current)) propertyProblems.push("Target kWh must be lower");
  if (!filled(p.place_id) && !(Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)) && p.latitude != null && p.longitude != null && (Number(p.latitude) !== 0 || Number(p.longitude) !== 0))) propertyProblems.push("Address not verified");
  const epcOptions = pc === "ES" ? ["A","B","C","D","E","F","G"] : ["A_PLUS","A","B","C","D","E","F"];
  if (filled(p.epc_rating_current) && !epcOptions.includes(p.epc_rating_current)) propertyProblems.push("Invalid current EPC");
  if (filled(p.epc_rating_target) && !epcOptions.includes(p.epc_rating_target)) propertyProblems.push("Invalid target EPC");
  if (filled(p.epc_rating_current) && filled(p.epc_rating_target) && epcOptions.includes(p.epc_rating_current) && epcOptions.includes(p.epc_rating_target) && epcOptions.indexOf(p.epc_rating_target) >= epcOptions.indexOf(p.epc_rating_current)) propertyProblems.push("Target EPC must improve");
  result["property-information"] = propertyProblems;

  const review = draft.review_download || {};
  result["review-download"] = review.confirmed && review.accepted_terms ? [] : ["Review confirmation and terms"];
  return Object.fromEntries(Object.entries(result).map(([step, errors]) => [step, { isValid: errors.length === 0, errors }]));
}

/** Only editable data that must be saved to Django. Exclude documents (uploaded
 * immediately) and computed read-only values to avoid false dirty prompts. */
export function dossierEditableFingerprint(draft = {}) {
  const borrowerFields = ["first_name","last_name","date_of_birth","nationality","country_of_residence","residency_status","residency_status_other","national_id_number","address","email","phone","marital_status","dependents","disability","preferred_contact_method"];
  const financialFields = ["net_income","listing_in_cpk","monthly_expenses","current_rent","employment_status","contract_type","employment_contracts_count","politically_exposed_person"];
  const sharedFields = ["equity_amount","property_price","tenure_years","repayment_bank_account","loan_disbursement_bank_account","notes"];
  const propertyFields = ["property_type","building_condition","surface_sqm","year_of_build","epc_rating_current","epc_rating_target","kwh_sqm_year_current","kwh_sqm_year_target","heating_type","cooling_type","ownership_type","street_name","house_number","city","postal_code","country","land_register_id","full_address","place_id","latitude","longitude"];
  const pick = (obj, keys) => Object.fromEntries(keys.map((key) => [key, String(obj?.[key] ?? "").trim()]));
  return JSON.stringify({
    market: String(draft.market_region_id ?? ""), country: String(draft.country_of_property_purchase ?? ""),
    borrowers: (draft.borrowers || []).map((b) => ({ role: b.role, personal: pick(b.personal_info, borrowerFields), financial: pick(b.financial_info, financialFields) })),
    shared: pick(draft.dossier_financial_info, sharedFields),
    property: pick(draft.property_information, propertyFields),
  });
}
